import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Checkbox,
  useMediaQuery,
  useTheme,
  InputAdornment,
  IconButton,
  Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MovieCard from '../components/MovieCard';
import ColorPalette from '../components/ColorPalette';
import {
  getAllMovies,
  searchTMDB,
  extractColors,
  applyTheme,
  getRooms,
  getZones,
  getLights,
  getSettings,
  updateSettings,
} from '../services/api';

function Movies() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [localMovies, setLocalMovies] = useState([]);
  const [tmdbResults, setTmdbResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [colorDialogOpen, setColorDialogOpen] = useState(false);
  const [targetDialogOpen, setTargetDialogOpen] = useState(false);
  const [pendingTheme, setPendingTheme] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [zones, setZones] = useState([]);
  const [lights, setLights] = useState([]);
  const [targetType, setTargetType] = useState('all');
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [defaultTarget, setDefaultTarget] = useState(null);
  const [setAsDefault, setSetAsDefault] = useState(false);

  useEffect(() => {
    loadLocalMovies();
    loadDefaultTarget();
  }, []);

  const loadLocalMovies = async () => {
    try {
      setLoading(true);
      const response = await getAllMovies();
      setLocalMovies(response.data);
    } catch (err) {
      console.error('Failed to load movies:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDefaultTarget = async () => {
    try {
      const response = await getSettings();
      const defaultTargetSetting = response.data.default_target;
      if (defaultTargetSetting) {
        try {
          const parsed = JSON.parse(defaultTargetSetting);
          setDefaultTarget(parsed);
        } catch (e) {
          console.error('Failed to parse default target:', e);
        }
      }
    } catch (err) {
      console.error('Failed to load default target:', err);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      handleClearSearch();
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setIsSearching(true);

      const response = await searchTMDB(searchQuery);
      setTmdbResults(response.data);
    } catch (err) {
      setError('Search failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setIsSearching(false);
    setTmdbResults([]);
  };

  const handleApplyTheme = async (movie) => {
    try {
      setSuccess(null);
      setError(null);
      setLoading(true);

      let colors = movie.colors;

      // If no colors, extract them first
      if (!colors) {
        const extractResponse = await extractColors({
          tmdb_id: movie.id || movie.tmdb_id,
          title: movie.title,
          poster_path: movie.poster_path,
          release_date: movie.release_date,
        });
        colors = extractResponse.data;
      }

      // Store colors and movie for later use
      setPendingTheme({ colors, movie });

      // Load rooms, zones, and lights
      const [roomsRes, zonesRes, lightsRes] = await Promise.all([
        getRooms(),
        getZones(),
        getLights(),
      ]);

      setRooms(roomsRes.data);
      setZones(zonesRes.data);
      setLights(lightsRes.data);

      // Apply default target if available
      if (defaultTarget) {
        setTargetType(defaultTarget.type);
        setSelectedTarget(defaultTarget.id);
      } else {
        setTargetType('all');
        setSelectedTarget(null);
      }
      setSetAsDefault(false);

      // Show target selection dialog
      setLoading(false);
      setTargetDialogOpen(true);
    } catch (err) {
      setError('Failed to prepare theme: ' + err.message);
      setLoading(false);
    }
  };

  const handleConfirmApplyTheme = async () => {
    if (!pendingTheme) return;

    try {
      setSuccess(null);
      setError(null);
      setLoading(true);
      setTargetDialogOpen(false);

      const { colors, movie } = pendingTheme;

      // Determine target light IDs based on selection
      let targetLightIds = null;

      if (targetType === 'room' && selectedTarget) {
        const room = rooms.find(r => r.id === selectedTarget);
        targetLightIds = room ? room.lights : null;
      } else if (targetType === 'zone' && selectedTarget) {
        const zone = zones.find(z => z.id === selectedTarget);
        targetLightIds = zone ? zone.lights : null;
      }

      // Prepare movie data for saving
      const movieData = {
        title: movie.title,
        poster_path: movie.poster_path,
        release_date: movie.release_date,
        year: movie.year
      };

      // Apply theme (this will now save the movie to database)
      await applyTheme(colors, movie.id || movie.tmdb_id, targetLightIds, movieData);

      // Notify Layout that a theme was applied (for Restore Lights button)
      window.dispatchEvent(new Event('themeApplied'));

      // Save as default if checkbox is checked
      if (setAsDefault) {
        const defaultTargetData = {
          type: targetType,
          id: targetType === 'all' ? null : selectedTarget,
        };
        try {
          await updateSettings({
            default_target: JSON.stringify(defaultTargetData),
          });
          setDefaultTarget(defaultTargetData);
        } catch (err) {
          console.error('Failed to save default target:', err);
        }
      }

      const targetName = targetType === 'all'
        ? 'all lights'
        : targetType === 'room'
        ? rooms.find(r => r.id === selectedTarget)?.name
        : zones.find(z => z.id === selectedTarget)?.name;

      setSuccess(`Applied theme for ${movie.title} to ${targetName}!`);

      // Reload local movies to update the list
      loadLocalMovies();

      // Clear search after applying a theme from search results
      if (isSearching) {
        handleClearSearch();
      }

      // Reset selections (but keep default target)
      if (!defaultTarget) {
        setTargetType('all');
        setSelectedTarget(null);
      } else {
        setTargetType(defaultTarget.type);
        setSelectedTarget(defaultTarget.id);
      }
      setSetAsDefault(false);
      setPendingTheme(null);
    } catch (err) {
      setError('Failed to apply theme: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleShowColors = async (movie) => {
    try {
      let movieWithColors = movie;

      // If no colors, extract them first
      if (!movie.colors) {
        setLoading(true);
        const extractResponse = await extractColors({
          tmdb_id: movie.id || movie.tmdb_id,
          title: movie.title,
          poster_path: movie.poster_path,
          release_date: movie.release_date,
        });
        movieWithColors = { ...movie, colors: extractResponse.data };
        setLoading(false);
      }

      setSelectedMovie(movieWithColors);
      setColorDialogOpen(true);
    } catch (err) {
      setError('Failed to extract colors: ' + err.message);
      setLoading(false);
    }
  };

  // Show TMDB results when searching, otherwise show local movies
  const movies = isSearching ? tmdbResults : localMovies;
  const showingSearchResults = isSearching && tmdbResults.length > 0;
  const noSearchResults = isSearching && tmdbResults.length === 0 && !loading;

  return (
    <Box>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
      >
        Movies
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Search for new movies or select from your collection to apply lighting themes.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 3
        }}
      >
        <TextField
          fullWidth
          label="Search for a movie"
          placeholder="Type a movie name and press Enter..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          size={isMobile ? "small" : "medium"}
          InputProps={{
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  edge="end"
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={handleSearch}
          disabled={loading || !searchQuery.trim()}
          sx={{
            minWidth: { xs: '100%', sm: 'auto' },
            whiteSpace: 'nowrap'
          }}
        >
          Search
        </Button>
      </Box>

      {/* Section Header */}
      <Typography
        variant="h6"
        sx={{
          mb: 2,
          color: 'text.secondary',
          fontSize: { xs: '1rem', sm: '1.25rem' }
        }}
      >
        {isSearching ? 'Search Results' : 'My Movies'}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : noSearchResults ? (
        <Paper sx={{ p: { xs: 2, sm: 4 }, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No movies found for "{searchQuery}". Try a different search term.
          </Typography>
        </Paper>
      ) : movies.length === 0 && !isSearching ? (
        <Paper sx={{ p: { xs: 2, sm: 4 }, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No movies yet. Search for a movie above to get started!
          </Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(4, 1fr)',
              lg: 'repeat(5, 1fr)',
            },
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          {movies.map((movie) => (
            <MovieCard
              key={movie.id || movie.tmdb_id}
              movie={movie}
              onApplyTheme={handleApplyTheme}
              onShowColors={handleShowColors}
            />
          ))}
        </Box>
      )}

      {/* Color Palette Dialog */}
      <Dialog
        open={colorDialogOpen}
        onClose={() => setColorDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {selectedMovie?.title} - Color Palette
        </DialogTitle>
        <DialogContent>
          {selectedMovie?.colors && (
            <ColorPalette colors={selectedMovie.colors} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColorDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              handleApplyTheme(selectedMovie);
              setColorDialogOpen(false);
            }}
          >
            Apply Theme
          </Button>
        </DialogActions>
      </Dialog>

      {/* Target Selection Dialog */}
      <Dialog
        open={targetDialogOpen}
        onClose={() => setTargetDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Select Target Lights
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose where to apply the theme
          </Typography>

          <RadioGroup
            value={targetType}
            onChange={(e) => {
              setTargetType(e.target.value);
              setSelectedTarget(null);
            }}
          >
            <FormControlLabel
              value="all"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {`All Lights (${lights.length} lights)`}
                  {defaultTarget?.type === 'all' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
                      <CheckCircleIcon sx={{ fontSize: 16 }} />
                      <Typography variant="caption" color="success.main">default</Typography>
                    </Box>
                  )}
                </Box>
              }
            />

            {rooms.length > 0 && (
              <>
                <FormControlLabel
                  value="room"
                  control={<Radio />}
                  label="Specific Room"
                />
                {targetType === 'room' && (
                  <List sx={{ pl: { xs: 2, sm: 4 } }}>
                    {rooms.map((room) => (
                      <ListItem key={room.id} disablePadding>
                        <ListItemButton
                          selected={selectedTarget === room.id}
                          onClick={() => setSelectedTarget(room.id)}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                {room.name}
                                {defaultTarget?.type === 'room' && defaultTarget?.id === room.id && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
                                    <CheckCircleIcon sx={{ fontSize: 14 }} />
                                    <Typography variant="caption" color="success.main">default</Typography>
                                  </Box>
                                )}
                              </Box>
                            }
                            secondary={`${room.lights.length} lights`}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            )}

            {zones.length > 0 && (
              <>
                <FormControlLabel
                  value="zone"
                  control={<Radio />}
                  label="Specific Zone"
                />
                {targetType === 'zone' && (
                  <List sx={{ pl: { xs: 2, sm: 4 } }}>
                    {zones.map((zone) => (
                      <ListItem key={zone.id} disablePadding>
                        <ListItemButton
                          selected={selectedTarget === zone.id}
                          onClick={() => setSelectedTarget(zone.id)}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                {zone.name}
                                {defaultTarget?.type === 'zone' && defaultTarget?.id === zone.id && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
                                    <CheckCircleIcon sx={{ fontSize: 14 }} />
                                    <Typography variant="caption" color="success.main">default</Typography>
                                  </Box>
                                )}
                              </Box>
                            }
                            secondary={`${zone.lights.length} lights`}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            )}
          </RadioGroup>

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={
              <Checkbox
                checked={setAsDefault}
                onChange={(e) => setSetAsDefault(e.target.checked)}
              />
            }
            label="Set as default target"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTargetDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmApplyTheme}
            disabled={
              loading ||
              (targetType === 'room' && !selectedTarget) ||
              (targetType === 'zone' && !selectedTarget)
            }
          >
            Apply Theme
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Movies;
